import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FilterControls = ({ 
  filters, 
  onFilterChange, 
  onGenerateWeeklyCheckin,
  exerciseCount = 0,
  isGeneratingCheckin = false,
  className = '' 
}) => {
  const sortOptions = [
    { value: 'dueDate', label: 'Due Date' },
    { value: 'title', label: 'Title (A-Z)' },
    { value: 'duration', label: 'Duration' },
    { value: 'status', label: 'Status' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Exercises' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'due-today', label: 'Due Today' },
    { value: 'due-soon', label: 'Due Soon' }
  ];

  const durationOptions = [
    { value: 'all', label: 'Any Duration' },
    { value: 'short', label: 'Short (≤15 min)' },
    { value: 'medium', label: 'Medium (16-30 min)' },
    { value: 'long', label: 'Long (>30 min)' }
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFilterChange({
      sortBy: 'dueDate',
      status: 'all',
      duration: 'all',
      searchQuery: ''
    });
  };

  const hasActiveFilters = filters?.status !== 'all' || 
                          filters?.duration !== 'all' || 
                          filters?.searchQuery?.trim() !== '';

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Exercise Management
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {exerciseCount} exercise{exerciseCount !== 1 ? 's' : ''}
            </span>
            <Button
              variant="default"
              size="sm"
              onClick={onGenerateWeeklyCheckin}
              loading={isGeneratingCheckin}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Generate Weekly Check-in
            </Button>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            placeholder="Search exercises..."
            value={filters?.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {filters?.searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleFilterChange('searchQuery', '')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
            >
              <Icon name="X" size={14} />
            </Button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Sort by"
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
          />

          <Select
            label="Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />

          <Select
            label="Duration"
            options={durationOptions}
            value={filters?.duration}
            onChange={(value) => handleFilterChange('duration', value)}
          />

          <div className="flex items-end">
            <Button
              variant="outline"
              size="default"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              iconName="RotateCcw"
              iconPosition="left"
              fullWidth
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex items-center space-x-2 pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            <div className="flex items-center space-x-2 flex-wrap">
              {filters?.status !== 'all' && (
                <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                  <span>Status: {statusOptions?.find(opt => opt?.value === filters?.status)?.label}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFilterChange('status', 'all')}
                    className="p-0 h-4 w-4 hover:bg-primary/20"
                  >
                    <Icon name="X" size={10} />
                  </Button>
                </span>
              )}
              
              {filters?.duration !== 'all' && (
                <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                  <span>Duration: {durationOptions?.find(opt => opt?.value === filters?.duration)?.label}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFilterChange('duration', 'all')}
                    className="p-0 h-4 w-4 hover:bg-primary/20"
                  >
                    <Icon name="X" size={10} />
                  </Button>
                </span>
              )}
              
              {filters?.searchQuery?.trim() && (
                <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                  <span>Search: "{filters?.searchQuery}"</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFilterChange('searchQuery', '')}
                    className="p-0 h-4 w-4 hover:bg-primary/20"
                  >
                    <Icon name="X" size={10} />
                  </Button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterControls;